import { Locale } from '@finxone-platform/shared/enums';
import {
  AlertsAccountActionSubGroup,
  AlertsBeneficiaryActionSubGroup,
  AlertsCardActionSubGroup,
  AlertsDocumentActionSubGroup,
  AlertsFunctionality,
  AlertsHttpResponseSubGroup,
  AlertsIndividualFeeChargeActionSubGroup,
  AlertsMappingType,
  AlertsOnboardingSubGroup,
  AlertsOthersSettingSubGroup,
  AlertsPaymentActionSubGroup,
  AlertsProfileSubGroup,
  AlertsType,
  AlertsVerificationSubGroup,
} from '@finxone-platform/shared/sys-config-types';
import { accountTranslations, generateAccountAlerts } from './default/accounts';
import { beneficiariesTranslations, generateBeneficiariesAlerts } from './default/beneficiaries';
import { cardsTranslations, generateCardsAlerts } from './default/cards';
import { documentsTranslations, generateDocumentsAlerts } from './default/documents';
import { feeManagementTranslations, generateFeeManagementAlerts } from './default/fee-management';
import { generateHttpErrorAlerts, httpErrorTranslations } from './default/http-error';
import {
  generateOnboardingIndividualAlerts,
  onboardingIndividualTranslations,
} from './default/onboarding-individual';
import { generateOtherAlerts, otherTranslations } from './default/others';
import { generatePaymentsAlerts, paymentsTranslations } from './default/payments';
import { generateProfileAlerts, profileTranslations } from './default/profile';
import { AccountErrorCodes } from './error-codes/account.type';
import { BeneficiaryErrorCodes } from './error-codes/beneficiaries.type';
import { CardsErrorCodes } from './error-codes/cards.type';
import { DocumentsErrorCodes } from './error-codes/document.type';
import { FeeManagementErrorCodes } from './error-codes/fee-management.type';
import { HttpErrorCode } from './error-codes/http-error.type';
import { OnboardingIndividualErrorCodes } from './error-codes/onboarding-individual.type';
import { PaymentsErrorCodes } from './error-codes/payments.type';
import { ProfileErrorCodes } from './error-codes/profile.type';
import { EmailVerifyErrorCodes, PhoneNumberVerifyErrorCodes } from './error-codes/verification.type';
import { AccountSuccessCodes } from './success-codes/account.type';
import { BeneficiariesSuccessCodes } from './success-codes/beneficiaries.type';
import { CardsSuccessCodes } from './success-codes/cards.type';
import { DocumentsSuccessCodes } from './success-codes/document.type';
import { FeeManagementSuccessCodes } from './success-codes/fee-mangement.type';
import { NotificationsSuccessCodes } from './success-codes/notifications.type';
import { OnboardingIndividualSuccessCodes } from './success-codes/onboarding-individual.type';
import { OrganisationSuccessCodes } from './success-codes/others.type';
import { PaymentsSuccessCodes } from './success-codes/payments.type';
import { ProfileSuccessCodes } from './success-codes/profile.type';

export const defaultAlertsMapping: AlertsMappingType = {
  [Locale.en_GB]: {
    alerts: {
      [AlertsFunctionality.ONBOARDING]: {
        [AlertsOnboardingSubGroup.VERIFY_EMAIL]: [
          EmailVerifyErrorCodes.EMAIL_VERIFIED,
          EmailVerifyErrorCodes.WRONG_EMAIL_OTP,
        ],
        [AlertsOnboardingSubGroup.VERIFY_PHONE]: [
          PhoneNumberVerifyErrorCodes.PHONE_VERIFIED,
          PhoneNumberVerifyErrorCodes.WRONG_PHONE_OTP,
        ],
        [AlertsOnboardingSubGroup.ONBOARD_INDIVIDUAL]: [
          OnboardingIndividualErrorCodes.CREATING_ORG_FAILED,
          OnboardingIndividualErrorCodes.DUPLICATE_EMAIL,
          OnboardingIndividualErrorCodes.EXISTING_USER,
          OnboardingIndividualErrorCodes.ONBOARD_FAILED,
          OnboardingIndividualSuccessCodes.ONBOARD_SUCCESS,
          OnboardingIndividualErrorCodes.UNABLE_TO_ADD_USER,
          OnboardingIndividualErrorCodes.INVALID_TITLE,
          OnboardingIndividualErrorCodes.INVALID_PRODUCT_CODE,
          OnboardingIndividualErrorCodes.INVALID_DEPOSIT_AMOUNT,
          OnboardingIndividualErrorCodes.INVALID_SOURCE_OF_FUNDS,
          OnboardingIndividualErrorCodes.INVALID_PURPOSE_OF_FUNDS,
          OnboardingIndividualErrorCodes.COUNTY_IS_STRING,
          OnboardingIndividualErrorCodes.PRODUCT_UNAVAILABLE,
          OnboardingIndividualErrorCodes.COMPANY_NOT_FOUND,
          OnboardingIndividualErrorCodes.COMPANY_NOT_ACTIVE,
        ],
      },
      [AlertsFunctionality.FEE_MANAGEMENT]: {
        [AlertsIndividualFeeChargeActionSubGroup.FEE_RETRY]: [
          FeeManagementErrorCodes.FEE_CHARGE_ALREADY_APPLIED,
          FeeManagementSuccessCodes.FEE_RETRY_SUCCESSFUL,
        ],
        [AlertsIndividualFeeChargeActionSubGroup.FEE_REVERT]: [
          FeeManagementSuccessCodes.FEE_REVERT_SUCCESSFUL,
        ],
      },
      [AlertsFunctionality.PAYMENTS]: {
        [AlertsPaymentActionSubGroup.PAYMENT_REQUEST]: [
          PaymentsErrorCodes.PAYMENT_REQUEST_FORBIDDDEN,
          PaymentsErrorCodes.INITIATOR_REQUEST_ERROR,
          PaymentsErrorCodes.REQUEST_ORG_MISMATCH,
          PaymentsErrorCodes.PAYMENT_INITIATOR_FORBIDDDEN,
          PaymentsErrorCodes.PAYMENT_APPROVER_FORBIDDDEN,
          PaymentsErrorCodes.PAYMENT_REQUEST_ALREADY_PROCCESSED,
          PaymentsErrorCodes.PAYMENT_OWN_REQUEST_ERROR,
          PaymentsErrorCodes.PAYMENT_INVALID_ROLE_ERROR,
          PaymentsErrorCodes.ALL_PAYMENT_REQUESTS_FAILED,
          PaymentsErrorCodes.PAYMENT_RECORD_NOT_FOUND,
          PaymentsErrorCodes.PAYMENT_SUMMARY_NOT_FOUND,
          PaymentsErrorCodes.PAYMENT_READER_FORBIDDDEN,
        ],
        [AlertsPaymentActionSubGroup.TRANSFER_PAYMENT]: [
          PaymentsErrorCodes.INSUFFICIENT_BALANCE,
          PaymentsErrorCodes.TARGET2_BIC_AND_ADDRESS_REQUIRED,
          PaymentsErrorCodes.EXCEEDED_TRANSFER_LIMIT,
          PaymentsErrorCodes.PAYMENT_REQUEST_FAILED,
          PaymentsErrorCodes.INVALID_AMOUNT,
          PaymentsErrorCodes.AMOUNT_TOO_LOW,
          PaymentsErrorCodes.PAYMENT_FAILED_BIC_REQUIRED,
          PaymentsErrorCodes.PAYMENT_FAILED_ADDRESS_REQUIRED,
          PaymentsErrorCodes.CUSTOMER_IS_NOT_ENROLLED,
          PaymentsErrorCodes.EQUALS_EXCHANGE_NEXT_TRADING_DAY_REQUIRED,
          PaymentsErrorCodes.EQUALS_PAYMENT_EXCHANGE_TARGET_CURRENCY_UNRESOLVED,
          PaymentsErrorCodes.EQUALS_PAYMENT_EXCHANGE_UNSUPPORTED_FROM_SUB_BUDGET,
          PaymentsSuccessCodes.WITHDRAW_SUCCESS,
          PaymentsErrorCodes.SCHEDULED_PAYMENT_CANCELLED_FAILED,
          PaymentsErrorCodes.PAYMENTS_CANCELLED_FAILED,
          PaymentsSuccessCodes.SCHEDULED_PAYMENT_CANCELLED,
          PaymentsSuccessCodes.PAYMENTS_CANCELLED,
          PaymentsSuccessCodes.SUBMIT_WITHDRAWAL_NOTICE,
          PaymentsErrorCodes.FAIL_WITHDRAWAL_NOTICE,
          PaymentsErrorCodes.DESCRIPTION_LIMIT_VALIDATION_ERROR,
        ],
      },
      [AlertsFunctionality.PROFILE]: {
        [AlertsProfileSubGroup.UPDATE_PROFILE]: [
          ProfileErrorCodes.ADDRESS1_IS_STRING,
          ProfileErrorCodes.ADDRESS2_IS_STRING,
          ProfileErrorCodes.ADDRESS3_IS_STRING,
          ProfileErrorCodes.ADDRESS4_IS_STRING,
          ProfileErrorCodes.CITY_IS_STRING,
          ProfileErrorCodes.CITY_REQUIRED,
          ProfileErrorCodes.COUNTRY_IS_ISO31661ALPHA2,
          ProfileErrorCodes.COUNTRY_IS_STRING,
          ProfileErrorCodes.COUNTRY_REQUIRED,
          ProfileErrorCodes.DOB_IS_DATE_STRING,
          ProfileErrorCodes.DOB_IS_STRING,
          ProfileErrorCodes.DOB_REQUIRED,
          ProfileErrorCodes.EMAIL_INVALID,
          ProfileErrorCodes.EMAIL_REQUIRED,
          ProfileErrorCodes.EMAIL_VERIFIED_BOOLEAN,
          ProfileErrorCodes.FIRST_NAME_IS_STRING,
          ProfileErrorCodes.FIRST_NAME_REQUIRED,
          ProfileErrorCodes.FLAT_NAME_IS_STRING,
          ProfileErrorCodes.LAST_NAME_IS_STRING,
          ProfileErrorCodes.LAST_NAME_REQUIRED,
          ProfileErrorCodes.MIDDLE_NAME_IS_STRING,
          ProfileErrorCodes.ORGANISATION_ID_IS_STRING,
          ProfileErrorCodes.ORGANISATION_ID_IS_UUID,
          ProfileErrorCodes.PASSWORD_IS_STRING,
          ProfileErrorCodes.PASSWORD_REQUIRED,
          ProfileErrorCodes.PHONE_IS_STRING,
          ProfileErrorCodes.PHONE_IS_VALID,
          ProfileErrorCodes.PHONE_REQUIRED,
          ProfileErrorCodes.PHONE_VERIFIED_BOOLEAN,
          ProfileErrorCodes.POSTAL_CODE_IS_STRING,
          ProfileErrorCodes.POSTAL_CODE_REQUIRED,
          ProfileErrorCodes.PROPERTY_NAME_IS_STRING,
          ProfileErrorCodes.PROPERTY_NUMBER_IS_STRING,
          ProfileErrorCodes.ROLE_ID_IS_STRING,
          ProfileErrorCodes.USERNAME_IS_STRING,
          ProfileSuccessCodes.MARKETING_PREFERENCE_UPDATED,
          ProfileErrorCodes.PROFILE_OTP_VERIFICATION_REQUIRED,
          ProfileErrorCodes.PROFILE_UPDATE_LOCKOUT_DAYS,
          ProfileErrorCodes.PASSWORD_INSECURE,
          ProfileErrorCodes.PASSWORD_RESET_LINK_EXPIRED,
        ],
        [AlertsProfileSubGroup.CHANGE_PASSWORD]: [
          ProfileErrorCodes.PASSWORD_REQUIRED,
          ProfileErrorCodes.PASSWORD_MIN_LENGTH,
          ProfileErrorCodes.PASSWORD_MAX_LENGTH,
          ProfileErrorCodes.CURRENT_PASSWORD_REQUIRED,
          ProfileErrorCodes.CURRENT_PASSWORD_NOT_MATCH,
          ProfileErrorCodes.CONFIRM_PASSWORD_REQUIRED,
          ProfileErrorCodes.CONFIRM_PASSWORD_MIN_LENGTH,
          ProfileErrorCodes.CONFIRM_PASSWORD_MAX_LENGTH,
          ProfileErrorCodes.NEW_AND_CONFIRM_PASSWORD_NOT_MATCH,
          ProfileErrorCodes.NEW_AND_OLD_PASSWORD_SAME,
          ProfileErrorCodes.USER_NOT_EXIST,
        ],
        [AlertsProfileSubGroup.NOTIFICATIONS]: [ProfileErrorCodes.USER_NOT_EXIST],
        [AlertsProfileSubGroup.BANK_STATEMENT]: [ProfileErrorCodes.BANK_STATEMENT_MISSING_CURRENCY],
      },
      [AlertsFunctionality.CARDS]: {
        [AlertsCardActionSubGroup.CARD_MANAGEMENT]: [
          CardsErrorCodes.CARD_BLOCKED,
          CardsErrorCodes.CARD_DOES_NOT_EXIST,
          CardsErrorCodes.CARD_NOT_SPECIFIED,
          CardsErrorCodes.FAILED_TO_ADD_CARD,
          CardsErrorCodes.FAILED_TO_UPDATE_CARD,
          CardsErrorCodes.FAILED_TO_FETCH_CARD,
          CardsErrorCodes.FAILED_TO_FETCH_CARD_TRANSACTIONS,
          CardsErrorCodes.FAILED_TO_FETCH_LINKED_ACCOUNTS_FROM_CARD_PROVIDER,
          CardsSuccessCodes.CARD_NUMBER_COPIED,
          CardsSuccessCodes.CARD_CVV_COPIED,
          CardsSuccessCodes.CARD_EXP_DATE_COPIED,
          CardsSuccessCodes.CARD_RENAMED,
          CardsSuccessCodes.CARD_FROZEN,
          CardsSuccessCodes.CARD_UNFROZEN,
          CardsSuccessCodes.CARD_UNBLOCKED,
          CardsSuccessCodes.CARD_ACTIVATED,
          CardsErrorCodes.FAILED_TO_UNLOCK_CARD_PIN,
          CardsErrorCodes.FAILED_TO_REISSUE_CARD,
        ],
      },
      [AlertsFunctionality.BENEFICIARY]: {
        [AlertsBeneficiaryActionSubGroup.BENEFICIARY_MANAGEMENT]: [
          BeneficiaryErrorCodes.FAILED_TO_FETCH_CHECK_PAYEE,
          BeneficiaryErrorCodes.FAILED_TO_UPDATE_BENEFICIARY,
          BeneficiaryErrorCodes.FAILED_TO_CREATE_BENEFICIARY,
          BeneficiariesSuccessCodes.ADD_BENEFICIARY,
          BeneficiariesSuccessCodes.UPDATE_BENEFICIARY,
          BeneficiariesSuccessCodes.REMOVE_BENEFICIARY,
          BeneficiaryErrorCodes.INVALID_IBAN,
          BeneficiaryErrorCodes.INVALID_BIC,
          BeneficiaryErrorCodes.INVALID_ACCOUNT_NUMBER,
          BeneficiaryErrorCodes.INVALID_SORT_CODE,
          BeneficiariesSuccessCodes.ADD_FAVORITE_BENEFICIARY,
          BeneficiariesSuccessCodes.REMOVE_FAVORITE_BENEFICIARY,
          BeneficiaryErrorCodes.ALREADY_FAVORITE,
          BeneficiaryErrorCodes.NOT_IN_FAVORITE,
          BeneficiaryErrorCodes.FAILED_TO_MARK_FAVORITE,
          BeneficiaryErrorCodes.BENEFICIARY_NOT_FOUND,
          BeneficiaryErrorCodes.INVALID_ACCOUNT_DETAILS_COP,
        ],
      },
      [AlertsFunctionality.ACCOUNTS]: {
        [AlertsAccountActionSubGroup.ACCOUNT_MANAGEMENT]: [
          AccountErrorCodes.ACCOUNT_NICKNAME_UPDATE_FAILED,
          AccountErrorCodes.ACCOUNT_NICKNAME_MAX_LENGTH,
          AccountErrorCodes.ACCOUNT_ALREADY_CLOSED,
          AccountErrorCodes.ACCOUNT_ZERO_BALANCE,
          AccountErrorCodes.ACCOUNT_CLOSED_ERROR,
          AccountErrorCodes.ACCOUNT_CLOSE_COMPLIANCE_ERROR,
          AccountSuccessCodes.ACCOUNT_NICKNAME_UPDATED,
          AccountSuccessCodes.ACCOUNT_CLOSED,
          AccountErrorCodes.AMOUNT_NOT_VALID,
          AccountErrorCodes.MAX_DEPOSIT_LIMIT,
          AccountErrorCodes.APPLICANT_CHECK_FAILED,
          AccountErrorCodes.MATURITY_DECISION_FAILED,
          AccountSuccessCodes.MATURITY_DECISION_SUCCESS,
          AccountErrorCodes.STATEMENT_INVALID_DATE_FUTURE,
          AccountErrorCodes.DEPOSIT_VALIDATION_FAILED,
          AccountErrorCodes.INSTITUTION_ACTION_NOT_SUPPORTED,
          AccountErrorCodes.COP_CHECK_VERIFICATION_EXPIRED,
          AccountErrorCodes.NOMINATED_ACCOUNT_MISMATCH,
        ],
        [AlertsAccountActionSubGroup.ACCOUNT_OPENING]: [
          AccountSuccessCodes.ACCOUNT_OPEN_SUCCESS,
          AccountSuccessCodes.ACCOUNT_OPEN_REQUEST_SUCCESS,
        ],
      },
      [AlertsFunctionality.DOCUMENTS]: {
        [AlertsDocumentActionSubGroup.DOCUMENT_MANAGEMENT]: [
          DocumentsErrorCodes.DOCUMENT_ID_NOT_FOUND,
          DocumentsErrorCodes.DOCUMENT_TYPE_NOT_FOUND,
          DocumentsErrorCodes.DOCUMENT_FILE_ID_NOT_FOUND,
          DocumentsSuccessCodes.DOCUMENT_UPLOAD_SUCCESS,
          DocumentsErrorCodes.DOCUMENT_UPLOAD_FAILED,
          DocumentsErrorCodes.DOCUMENT_FILE_SIZE_EXCEEDED,
          DocumentsErrorCodes.DOCUMENT_GENERATION_FAILED,
          DocumentsErrorCodes.HOLDING_STATEMENT_NOT_IMPLEMENTED,
        ],
      },
      [AlertsFunctionality.HTTP_RESPONSE_CODE]: {
        [AlertsHttpResponseSubGroup.STATUS_FOUR_HUNDRED_CLIENT_ERRORS]: [
          HttpErrorCode.STATUS_400,
          HttpErrorCode.STATUS_401,
          HttpErrorCode.STATUS_402,
          HttpErrorCode.STATUS_403,
          HttpErrorCode.STATUS_404,
          HttpErrorCode.STATUS_405,
          HttpErrorCode.STATUS_4XX_CATCH_ALL_ERROR,
        ],
        [AlertsHttpResponseSubGroup.STATUS_FIVE_HUNDRED_SERVER_ERRORS]: [
          HttpErrorCode.STATUS_500,
          HttpErrorCode.STATUS_501,
          HttpErrorCode.STATUS_502,
          HttpErrorCode.STATUS_503,
          HttpErrorCode.STATUS_504,
          HttpErrorCode.STATUS_505,
          HttpErrorCode.STATUS_5XX_CATCH_ALL_ERROR,
        ],
      },
      [AlertsFunctionality.OTHERS]: {
        [AlertsOthersSettingSubGroup.NOTIFICATIONS_UPDATE]: [NotificationsSuccessCodes.UPDATE_SUCCESSFUL],
        [AlertsOthersSettingSubGroup.ORGANISATION]: [OrganisationSuccessCodes.ORGANISATION_SWITCH_SUCCESSFUL],
      },
    },
  },
};

export const defaultAlerts: AlertsType = {
  [Locale.en_GB]: {
    alerts: {
      ...generateFeeManagementAlerts(feeManagementTranslations[Locale.en_GB]),
      ...generateOnboardingIndividualAlerts(onboardingIndividualTranslations[Locale.en_GB]),
      ...generateCardsAlerts(cardsTranslations[Locale.en_GB]),
      ...generateBeneficiariesAlerts(beneficiariesTranslations[Locale.en_GB]),
      ...generatePaymentsAlerts(paymentsTranslations[Locale.en_GB]),
      ...generateProfileAlerts(profileTranslations[Locale.en_GB]),
      ...generateAccountAlerts(accountTranslations[Locale.en_GB]),
      ...generateOtherAlerts(otherTranslations[Locale.en_GB]),
      ...generateHttpErrorAlerts(httpErrorTranslations[Locale.en_GB]),
      ...generateDocumentsAlerts(documentsTranslations[Locale.en_GB]),
    },
  },
};

// use as a common type for returned values, add new values as the list increases
type RequestedSubgroupEnumType =
  | typeof AlertsOnboardingSubGroup
  | typeof AlertsProfileSubGroup
  | typeof AlertsVerificationSubGroup
  | typeof AlertsIndividualFeeChargeActionSubGroup
  | typeof AlertsPaymentActionSubGroup
  | typeof AlertsCardActionSubGroup
  | typeof AlertsBeneficiaryActionSubGroup
  | typeof AlertsAccountActionSubGroup
  | typeof AlertsHttpResponseSubGroup
  | typeof AlertsDocumentActionSubGroup
  | typeof AlertsOthersSettingSubGroup;

export const functionalityToSubgroupMap = new Map<AlertsFunctionality, RequestedSubgroupEnumType>([
  [AlertsFunctionality.ONBOARDING, AlertsOnboardingSubGroup],
  [AlertsFunctionality.PROFILE, AlertsProfileSubGroup],
  [AlertsFunctionality.VERIFICATION, AlertsVerificationSubGroup],
  [AlertsFunctionality.FEE_MANAGEMENT, AlertsIndividualFeeChargeActionSubGroup],
  [AlertsFunctionality.PAYMENTS, AlertsPaymentActionSubGroup],
  [AlertsFunctionality.CARDS, AlertsCardActionSubGroup],
  [AlertsFunctionality.BENEFICIARY, AlertsBeneficiaryActionSubGroup],
  [AlertsFunctionality.ACCOUNTS, AlertsAccountActionSubGroup],
  [AlertsFunctionality.HTTP_RESPONSE_CODE, AlertsHttpResponseSubGroup],
  [AlertsFunctionality.OTHERS, AlertsOthersSettingSubGroup],
  [AlertsFunctionality.DOCUMENTS, AlertsDocumentActionSubGroup],
]);
